import React from "react";
import styled, { keyframes } from "styled-components";

import Skeleton from "../../components/layout/skeleton";
import { breakpoints, colors } from "../../../theme";
import { ALL_RECOMMENDATIONS } from "../../utils/constants/recommendations";
import {
  Card,
  CardHeader,
  Avatar,
  NameBlock,
  BodyWrapper,
} from "./testimonial-card-styles";
import {
  CarouselRoot,
  CarouselViewport,
  CarouselTrack,
  CarouselItem,
  MasonryContainer,
  CardSlot,
  Controls,
} from "./testimonials-layout-styles";

const CARD_COUNT = ALL_RECOMMENDATIONS.length;

const pulse = keyframes`
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
`;

const AvatarBone = styled(Avatar)`
  background-color: ${colors.grey.medium};
  animation: ${pulse} 1.4s ease-in-out infinite;
`;

const NameBone = styled(Skeleton)`
  height: 20px;
  width: 55%;
`;

const RoleBone = styled(Skeleton)`
  height: 14px;
  width: 75%;
`;

const LineBone = styled(Skeleton)`
  height: 14px;
  margin-bottom: 10px;
`;

const BodyBones = styled.div`
  width: 100%;
`;

const ArrowBone = styled.div`
  box-sizing: border-box;
  flex-shrink: 0;
  height: 40px;
  width: 40px;
  border-radius: 50%;
  background-color: ${colors.grey.medium};
  animation: ${pulse} 1.4s ease-in-out infinite;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    height: 48px;
    width: 48px;
  }

  @media only screen and (min-width: ${breakpoints.desktop}) {
    height: 56px;
    width: 56px;
  }
`;

const ButtonBone = styled(Skeleton)`
  height: 56px;
  width: 180px;
  border-radius: 18px;
`;

const ExpandedCard = styled(Card)`
  min-height: 280px;
`;

const TestimonialCardSkeleton = ({ expanded = false }) => {
  const Shell = expanded ? ExpandedCard : Card;

  return (
    <Shell $expanded={expanded}>
      <CardHeader>
        <AvatarBone />
        <NameBlock>
          <NameBone />
          <RoleBone />
        </NameBlock>
      </CardHeader>
      <BodyWrapper $expanded={expanded}>
        <BodyBones>
          <LineBone width="100%" />
          <LineBone width="96%" />
          <LineBone width="92%" />
          <LineBone width="88%" />
          <LineBone width="70%" />
        </BodyBones>
      </BodyWrapper>
    </Shell>
  );
};

const CarouselTestimonialsSkeleton = ({ fullBleed = false }) => (
  <CarouselRoot data-testimonials aria-hidden $fullBleed={fullBleed}>
    <CarouselViewport $marquee={fullBleed}>
      <CarouselTrack $marquee={fullBleed}>
        {Array.from({ length: CARD_COUNT }, (_, index) => (
          <CarouselItem key={index}>
            <TestimonialCardSkeleton />
          </CarouselItem>
        ))}
      </CarouselTrack>
    </CarouselViewport>
    <Controls>
      {!fullBleed && <ArrowBone />}
      <ButtonBone />
      {!fullBleed && <ArrowBone />}
    </Controls>
  </CarouselRoot>
);

const MasonryTestimonialsSkeleton = () => (
  <MasonryContainer aria-hidden>
    {Array.from({ length: CARD_COUNT }, (_, index) => (
      <CardSlot key={index}>
        <TestimonialCardSkeleton expanded />
      </CardSlot>
    ))}
  </MasonryContainer>
);

const TestimonialsSkeleton = ({ expanded = false, fullBleed = false }) =>
  expanded ? (
    <MasonryTestimonialsSkeleton />
  ) : (
    <CarouselTestimonialsSkeleton fullBleed={fullBleed} />
  );

export default TestimonialsSkeleton;
